/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  CheckboxGroup,
  Link,
  LinkHighlight,
  MessageCallout,
  Spinner,
  Text,
} from '@sonarsource/echoes-react';
import { isEmpty } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { SharedDocLink, useSharedDocUrl } from '~adapters/helpers/docs';
import { getJiraWorkTypeMandatoryFields } from '../../helpers';
import { JiraWorkType } from '../../types';

interface JiraWorkTypesGroupProps {
  hasMandatoryFields?: boolean;
  isLoading?: boolean;
  onChange: (selectedWorkTypeIds: string[]) => void;
  selectedWorkTypeIds: string[];
  workTypes?: JiraWorkType[];
}

export function JiraProjectWorkTypesGroup(props: Readonly<JiraWorkTypesGroupProps>) {
  const { hasMandatoryFields, isLoading, onChange, selectedWorkTypeIds, workTypes } = props;
  const { formatMessage } = useIntl();
  const docUrl = useSharedDocUrl();

  return (
    <div className="sw-mt-6">
      <Spinner isLoading={isLoading}>
        {!isEmpty(workTypes) && (
          <>
            {hasMandatoryFields && (
              <MessageCallout
                action={
                  <Link
                    enableOpenInNewTab
                    highlight={LinkHighlight.CurrentColor}
                    to={docUrl(SharedDocLink.JiraIntegrationTroubleshooting)}
                  >
                    <FormattedMessage id="troubleshooting_steps" />
                  </Link>
                }
                title={formatMessage({
                  id: 'project_settings.jira_binding.work_types.mandatory_fields_warning.title',
                })}
                variety="info"
              >
                <FormattedMessage id="project_settings.jira_binding.work_types.mandatory_fields_warning.text" />
              </MessageCallout>
            )}

            <CheckboxGroup
              className="sw-mt-3"
              label={
                <Text as="p" className="sw-mb-3" isHighlighted>
                  <FormattedMessage id="project_settings.jira_binding.work_types.title" />
                </Text>
              }
              onChange={onChange}
              //@ts-expect-error options type expects a tuple, discussed with FEE squad, should be fixed at some point
              options={workTypes.map((workType) => {
                const mandatoryFields = getJiraWorkTypeMandatoryFields(workType);
                return {
                  label: workType.name,
                  value: workType.id,
                  isDisabled: mandatoryFields.length > 0,
                  helpText: mandatoryFields.length > 0 && (
                    <FormattedMessage
                      id="project_settings.jira_binding.work_types.mandatory_custom_fields"
                      values={{
                        fields: mandatoryFields.map((field) => field.name).join(', '),
                      }}
                    />
                  ),
                };
              })}
              value={selectedWorkTypeIds}
            />
          </>
        )}
      </Spinner>
    </div>
  );
}
